"use client";

import { motion } from "framer-motion";
import { Button } from "@/app/components/ui/button";
import Link from "next/link";
import { GradientLine } from "@/app/components/ui/gradient-line";

const BackgroundGlow = () => (
  <>
    {/* Top-right glow */}
    <motion.div
      className="absolute top-0 right-0 w-[800px] h-[800px] bg-[rgb(var(--color-neon-blue)_/_0.3)] rounded-full blur-[120px] -translate-y-1/4"
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.3, 0.4, 0.3],
        x: [0, 20, 0],
        y: [-100, -80, -100],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Bottom-left glow */}
    <motion.div
      className="absolute bottom-0 left-0 w-[700px] h-[700px] bg-[rgb(var(--color-electric-blue)_/_0.3)] rounded-full blur-[120px] translate-y-1/4"
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.3, 0.4, 0.3],
        x: [0, -30, 0],
        y: [100, 80, 100],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />

    {/* Center glow */}
    <motion.div
      className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-[rgb(var(--color-neon-green)_/_0.3)] rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2"
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.4, 0.3],
      }}
      transition={{
        duration: 12,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </>
);

export default function CTASection() {
  return (
    <section className="w-full bg-[rgb(var(--color-primary-dark)_/_0.95)] text-white relative overflow-hidden">
      <BackgroundGlow />

      {/* Grid Pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.02) 1px, transparent 1px),
                           linear-gradient(to bottom, rgb(255 255 255 / 0.02) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[rgb(var(--color-primary-dark))] via-transparent to-[rgb(var(--color-primary-dark))]" />

      <div className="container mx-auto px-4 relative py-40">
        <motion.div
          className="max-w-4xl mx-auto text-center space-y-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="space-y-6">
            <motion.span
              className="inline-flex px-4 py-2 rounded-full text-sm font-medium bg-[rgb(var(--color-neon-blue)_/_0.1)] text-[rgb(var(--color-neon-blue-light))] border border-[rgb(var(--color-neon-blue)_/_0.2)]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Get Started Today
            </motion.span>
            <h2 className="text-6xl font-bold tracking-[-0.02em] text-white">
              Ready to Transform Your IT Infrastructure?
            </h2>
            <p className="text-lg text-[rgb(var(--color-text-secondary))] max-w-2xl mx-auto tracking-[0.01em] leading-[1.8]">
              Let's discuss how we can help your business thrive with our
              comprehensive IT solutions.
            </p>
          </div>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <Button
              variant="neon"
              size="xl"
              className="w-full sm:w-auto relative overflow-hidden group"
              asChild
            >
              <Link href="/discover">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5 }}
                />
                <span className="relative flex items-center justify-center gap-2">
                  Schedule a Free Consultation
                  <motion.span
                    className="inline-block"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    →
                  </motion.span>
                </span>
              </Link>
            </Button>
            <Button
              variant="outline"
              size="xl"
              className="w-full sm:w-auto border-[rgb(var(--color-neon-blue)_/_0.3)] text-[rgb(var(--color-neon-blue-light))] hover:bg-[rgb(var(--color-neon-blue)_/_0.1)]"
              asChild
            >
              <Link href="/services/managed-it">Explore Our Services</Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Gradient Line */}
      <div className="absolute bottom-0 left-0 w-2/3">
        <GradientLine />
      </div>
    </section>
  );
}
